import { $isListItemNode, $isListNode } from '@lexical/list'
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import { $isHeadingNode } from '@lexical/rich-text'
import { $isRootTextContentEmpty } from '@lexical/text'
import { mergeRegister } from '@lexical/utils'
import {
    $getSelection,
    $isRangeSelection,
    COMMAND_PRIORITY_LOW,
    INSERT_LINE_BREAK_COMMAND,
    INSERT_PARAGRAPH_COMMAND,
    KEY_ENTER_COMMAND,
    LexicalEditor,
} from 'lexical'
import { useEffect } from 'react'

type EnterKeyPluginProps = {
    onSendMessage: (state: LexicalEditor) => void
}

const $isInsideFormattedBlock = () => {
    const selection = $getSelection()
    if (!$isRangeSelection(selection)) {
        return false
    }
    const node = selection.anchor.getNode()
    const parentNode = node.getParent()
    const isInsideList = $isListItemNode(parentNode) || $isListNode(parentNode) || $isHeadingNode(parentNode)
    return isInsideList
}

export const EnterKeyPlugin = ({ onSendMessage }: EnterKeyPluginProps) => {
    const [editor] = useLexicalComposerContext()

    useEffect(
        () =>
            mergeRegister(
                editor.registerCommand(
                    KEY_ENTER_COMMAND,
                    (event: KeyboardEvent | null) => {
                        const insideBlock = $isInsideFormattedBlock()

                        if ($isRootTextContentEmpty(editor.isComposing())) {
                            return false
                        }

                        if (event && !event.shiftKey && !event.ctrlKey && !event.metaKey && !event.altKey) {
                            // If inside a formatted block, let Lexical handle the Enter key normally
                            // such that we dont send the message if user intends to go to next line in a formatted block
                            if (insideBlock) {
                                return false
                            }

                            // Otherwise, send the message
                            event.preventDefault()

                            // commands are executed within an editor.update()-call,
                            // onSendMessage may want to do updates, and we should not call .update within .update()
                            // (ref: https://lexical.dev/docs/concepts/commands#editorregistercommand).
                            // So we escape current .update() context by waiting until the next tick
                            setTimeout(() => {
                                void onSendMessage(editor)
                            }, 0)
                            return true
                        }
                        return false
                    },
                    COMMAND_PRIORITY_LOW
                ),
                editor.registerCommand(
                    INSERT_LINE_BREAK_COMMAND,
                    () => {
                        // allow shift+enter to create eg. new list items
                        const isInsideBlock = $isInsideFormattedBlock()
                        if (isInsideBlock) {
                            editor.dispatchCommand(INSERT_PARAGRAPH_COMMAND, undefined)
                            return true
                        }
                        return false
                    },
                    COMMAND_PRIORITY_LOW
                )
            ),
        [editor, onSendMessage]
    )

    return null
}
